import React, { useCallback } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useForm } from 'react-hook-form';

import { Box, Button, Container, DialogActions, Stack, TextField } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import { createChannelMarkup } from '~/services/PromoService';

const isValidQueryParamsRegex = /^[^\s]+$/;

type FormValues = {
  source: string;
  medium: string;
  campaign: string;
  term?: string;
  markup: string;
  luxplus_markup?: string;
};

export default function ChannelMarkupCreatePage() {
  const { enqueueSnackbar } = useSnackbar();
  const tenant = useCurrentTenant();
  const { push, goBack } = useAppNavigation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>();

  const handleCreate = useCallback(
    async (data: FormValues) => {
      const luxplusMarkup =
        data.luxplus_markup !== undefined && data.luxplus_markup !== ''
          ? { luxplus_markup: Number(data.luxplus_markup) }
          : {};

      const payload = {
        source: data.source,
        medium: data.medium,
        campaign: data.campaign,
        ...(data.term ? { term: data.term } : {}),
        markup: Number(data.markup),
        brand: tenant.tenant.brand,
        ...luxplusMarkup,
      };

      const response = await createChannelMarkup(payload);
      if (response.status !== 201) {
        enqueueSnackbar('Channel Markup creation failed', { variant: 'error' });
        return;
      }

      reset();
      enqueueSnackbar('Channel Markup created successfully', { variant: 'success' });
      push('/marketing/channel-markup');
    },
    [enqueueSnackbar, push, reset, tenant.tenant.brand],
  );

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Channel Markup | Create</title>
      </Helmet>

      <PageSubheader title="Create Channel Markup" />

      <Box component="form" onSubmit={handleSubmit(handleCreate)} mt={2}>
        <Stack spacing={2} maxWidth={640}>
          <TextField
            id="source"
            label="Source"
            variant="outlined"
            {...register('source', {
              required: 'Source is required',
              pattern: { value: isValidQueryParamsRegex, message: 'utm_source' },
            })}
            error={!!errors.source}
            helperText={errors.source?.message}
          />
          <TextField
            id="medium"
            label="Medium"
            variant="outlined"
            {...register('medium', {
              required: 'Medium is required',
              pattern: { value: isValidQueryParamsRegex, message: 'utm_medium' },
            })}
            error={!!errors.medium}
            helperText={errors.medium?.message}
          />
          <TextField
            id="campaign"
            label="Campaign"
            variant="outlined"
            {...register('campaign', {
              required: 'Campaign is required',
              pattern: { value: isValidQueryParamsRegex, message: 'utm_campaign' },
            })}
            error={!!errors.campaign}
            helperText={errors.campaign?.message}
          />
          <TextField
            id="term"
            label="Term"
            variant="outlined"
            {...register('term', {
              pattern: { value: isValidQueryParamsRegex, message: 'utm_term' },
            })}
            error={!!errors.term}
            helperText={errors.term?.message}
          />
          <TextField
            id="markup"
            label="Markup"
            variant="outlined"
            {...register('markup', {
              required: 'Markup is required',
              validate: (value) => {
                const num = parseFloat(value);
                if (isNaN(num)) {
                  return 'Enter a valid number';
                }
                if (num < 0.95 || num > 1.1) {
                  return 'Enter a number between 0.95 and 1.10';
                }
                return true;
              },
            })}
            error={!!errors.markup}
            helperText={errors.markup?.message}
          />
          <TextField
            id="luxplus_markup"
            label="LuxPlus Markup"
            variant="outlined"
            {...register('luxplus_markup', {
              validate: (value) => {
                if (value === undefined || value === null || value === '') {
                  return true;
                }
                const num = parseFloat(value);
                if (isNaN(num)) {
                  return 'Enter a valid number';
                }
                if (num < 0.95 || num > 1.1) {
                  return 'Enter a number between 0.95 and 1.10';
                }
                return true;
              },
            })}
            error={!!errors.luxplus_markup}
            helperText={errors.luxplus_markup?.message}
          />
        </Stack>

        <DialogActions sx={{ px: 0, pt: 3 }}>
          <Button onClick={() => goBack()}>Cancel</Button>
          <Button variant="contained" type="submit">
            Create
          </Button>
        </DialogActions>
      </Box>
    </Container>
  );
}
